import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Coins, RefreshCw, Loader2, Check, Info, Percent, Save, Globe, ArrowUp } from "lucide-react";

const SYMBOL = { USD: "$", EUR: "€", GBP: "£" };

export default function Fx() {
  const { user: me } = useAuth();
  const canDiscount = me?.role === "admin" || me?.role === "assistant";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [manual, setManual] = useState({});
  const [savingCur, setSavingCur] = useState(null);
  const [disc, setDisc] = useState("");
  const [savingDisc, setSavingDisc] = useState(false);

  const apply = useCallback((d) => { setData(d); setDisc(String(d.discount_pct ?? 20)); }, []);
  const load = useCallback(() => api.get("/fx").then((r) => apply(r.data)).catch(() => {}).finally(() => setLoading(false)), [apply]);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try { const { data } = await api.post("/fx/refresh"); apply(data); toast.success("Kurlar yenilendi"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setRefreshing(false); }
  };

  const saveDiscount = async () => {
    const v = parseFloat(disc);
    if (isNaN(v) || v < 0 || v > 90) return toast.error("İskonto %0 ile %90 arasında olmalı");
    setSavingDisc(true);
    try { const { data } = await api.put("/fx/discount", { discount_pct: v }); apply(data); toast.success(`İskonto %${v} olarak kaydedildi`); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingDisc(false); }
  };

  const saveManual = async (cur) => {
    setSavingCur(cur);
    const val = parseFloat(manual[cur]);
    try {
      const { data } = await api.put(`/fx/${cur}`, { manual_rate: isNaN(val) ? null : val });
      apply(data);
      toast.success(isNaN(val) ? `${cur} manuel kuru kaldırıldı` : `${cur} manuel kuru kaydedildi`);
      setManual((m) => ({ ...m, [cur]: "" }));
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingCur(null); }
  };

  const currencies = data?.currencies ? Object.values(data.currencies) : [];

  return (
    <div className="space-y-4 fade-in">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Coins className="h-5 w-5 text-brand-green" />
          <div><p className="overline">Kasa</p><h1 className="font-heading text-2xl font-bold text-slate-900">Döviz Kurları</h1></div>
        </div>
        <Button data-testid="fx-refresh-btn" onClick={refresh} disabled={refreshing} className="bg-brand-green text-white hover:bg-brand-green-600">
          {refreshing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Kurları Yenile
        </Button>
      </div>

      {/* Discount rule */}
      <div className="flex flex-col gap-3 rounded-md border border-orange-200 bg-orange-50 p-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-2 text-sm text-orange-800">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <p>Dövizli nakit ödemede müşterinin dövizi <b>piyasa ortalamasının %{data?.discount_pct ?? 20} eksiği</b> ile kabul edilir ve kur <b>yukarı yönlü tam TL'ye yuvarlanarak (düzlenmiş)</b> sabitlenir; para üstü daima TL verilir.</p>
        </div>
        {canDiscount && (
          <div className="flex shrink-0 items-center gap-2">
            <Percent className="h-4 w-4 text-orange-500" />
            <Input data-testid="fx-discount-input" type="number" value={disc} onChange={(e) => setDisc(e.target.value)} className="h-9 w-24 tabular" placeholder="%" />
            <Button data-testid="fx-discount-save" onClick={saveDiscount} disabled={savingDisc} size="sm" className="h-9 bg-brand-orange text-white hover:bg-orange-600">
              {savingDisc ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />} Kaydet
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-44 animate-pulse rounded-md bg-slate-200/60" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {currencies.map((c) => (
            <div key={c.currency} data-testid={`fx-card-${c.currency}`} className="rounded-md bg-white p-4 ring-1 ring-inset ring-slate-200">
              <div className="mb-3 flex items-center justify-between">
                <p className="font-heading text-lg font-bold text-slate-800">{SYMBOL[c.currency]} {c.currency}</p>
                <span className={`rounded-sm px-1.5 py-0.5 text-[10px] font-bold ${c.manual ? "bg-brand-orange text-white" : "bg-slate-100 text-slate-500"}`}>
                  {c.manual ? "MANUEL" : c.source}
                </span>
              </div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Piyasa Ort.</span><span className="tabular font-medium text-slate-700">{c.market_avg?.toFixed(2)} ₺</span></div>
                <div className="flex justify-between"><span className="text-slate-500">−%{c.discount_pct} Kur</span><span className="tabular font-medium text-slate-700">{c.effective_live?.toFixed(2)} ₺</span></div>
                {!c.manual && c.effective_rounded != null && (
                  <div className="flex justify-between"><span className="flex items-center gap-1 text-slate-500"><ArrowUp className="h-3 w-3 text-emerald-600" />Düzlenmiş (yukarı)</span><span data-testid={`fx-rounded-${c.currency}`} className="tabular font-semibold text-emerald-700">{c.effective_rounded?.toFixed(2)} ₺</span></div>
                )}
                <div className="flex justify-between border-t border-slate-100 pt-1.5"><span className="font-semibold text-slate-700">Kullanılan</span><span data-testid={`fx-used-${c.currency}`} className="tabular text-base font-bold text-brand-green">{c.used?.toFixed(2)} ₺</span></div>
              </div>
              <div className="mt-3 flex gap-2">
                <Input data-testid={`fx-manual-${c.currency}`} type="number" value={manual[c.currency] ?? ""}
                  onChange={(e) => setManual((m) => ({ ...m, [c.currency]: e.target.value }))}
                  placeholder={c.manual ? `Manuel: ${c.manual}` : "Manuel kur"} className="h-9 tabular" />
                <Button data-testid={`fx-save-${c.currency}`} onClick={() => saveManual(c.currency)} disabled={savingCur === c.currency}
                  size="sm" variant="outline" className="h-9 shrink-0">
                  {savingCur === c.currency ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                </Button>
              </div>
              {c.manual != null && (
                <button data-testid={`fx-clear-${c.currency}`} onClick={() => { setManual((m) => ({ ...m, [c.currency]: "" })); saveManual(c.currency); }}
                  className="mt-1.5 text-[11px] text-slate-400 hover:text-red-500">manuel kuru kaldır</button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Source references */}
      <div className="flex items-start gap-2 rounded-md bg-slate-50 p-3 text-[11px] text-slate-500">
        <Globe className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
        <p data-testid="fx-sources">
          Canlı ortalama {data?.source_count ?? 0} kaynaktan hesaplanır
          {data?.sources?.length ? ` — ${data.sources.join(", ")}` : " (şu an tüm kaynaklar çevrimdışı; varsayılan kurlar kullanılıyor)"}.
          Kaynaklar gün içinde otomatik güncellenir.
        </p>
      </div>
    </div>
  );
}
